import React, { memo } from 'react';
import { Box, Typography, useTheme } from '@material-ui/core';
import { useTranslate } from 'react-admin';
import { Alert } from '@material-ui/lab';
import { QRCode } from 'antd';
import { useDispatch } from 'react-redux';
import useCountdown from '../../../../base/hooks/useCountdown';
import { usePayment } from '../../hooks';
import { formatMoney } from '../../../../services/util/formatNumber';
import { showConfirmDialog } from '../../../../services/redux/app/app.actions';
import CopyButton from '../../../../base/components/mui/CopyButton';

const PaymentTransfer = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { paymentChannel, totalPayableAmount, paymentData, currencyCode, setInvoiceIds } = usePayment();
  const t = useTranslate();

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        gap: 8,
      }}
    >
      <Box fontSize={18} fontWeight={700}>
        {t(`common.paymentChannel.${paymentChannel}`)}
      </Box>
      <Box width="100%" display="flex" justifyContent="space-between" fontSize={16}>
        <Box fontWeight={600}>{t('resources.payment.fields.id')}</Box>
        <Box
          display="flex"
          sx={{
            gap: 2,
          }}
          alignItems="center"
          fontWeight={600}
        >
          {paymentData?.paymentId}
          {!!paymentData?.paymentId && <CopyButton content={paymentData.paymentId} size="small" />}
        </Box>
      </Box>
      <Box width="100%" display="flex" justifyContent="space-between" fontSize={16}>
        <Box fontWeight="bold">
          {t('resources.invoice.totalInvoiceAmount', {
            currencyCode,
          })}
        </Box>
        <Box fontWeight="bold">{formatMoney(totalPayableAmount, currencyCode)}</Box>
      </Box>
      <Box width="100%" display="flex" justifyContent="space-between" color={theme.palette.primary.main} fontSize={16}>
        <Box fontWeight="bold">{t('resources.invoice.usdtPayableAmount')}</Box>
        <Box fontWeight="bold">{formatMoney(totalPayableAmount, 'USDT')}</Box>
      </Box>
      <Box component={Alert} severity="warning" icon={false} mb={2} mt={1}>
        {t('resources.invoice.paymentTransferWarning')}
      </Box>
      <Box component={Typography} variant="h4">
        {t(`resources.invoice.usdtAddress.${paymentChannel}`)}
      </Box>
      <Box
        display="flex"
        alignItems="center"
        sx={{
          gap: 18,
        }}
      >
        <QRCode value={paymentData?.toAddress || ''} size={220} />
        <Box
          display="flex"
          flexDirection="column"
          height="fit-content"
          sx={{
            gap: 4,
          }}
        >
          <ExpireCountdown
            onExpire={() => {
              dispatch(
                showConfirmDialog({
                  isOpen: true,
                  title: 'resources.invoice.expiredTitle',
                  content: 'resources.invoice.expiredMessage',
                  onConfirm: () => {
                    setInvoiceIds([]);
                  },
                  onClose: () => {
                    setInvoiceIds([]);
                  },
                }),
              );
            }}
            expireTime={paymentData?.expirationTime || 0}
            showHour={false}
          />
          <Box
            display="flex"
            sx={{
              gap: 2,
            }}
            alignItems="center"
          >
            {paymentData?.toAddress || ''}
            {!!paymentData?.toAddress && <CopyButton content={paymentData.toAddress} size="small" />}
          </Box>
        </Box>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        sx={{
          gap: 2,
        }}
        mb={3}
        mt={1}
      >
        {[1, 2, 3].map((index) => (
          <Box key={index} fontWeight={500}>
            {`${index}. ${t(`resources.invoice.transferSteps.${index}`)}`}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default memo(PaymentTransfer);

const ExpireCountdown = ({ expireTime, showHour, onExpire }) => {
  const { time, expired } = useCountdown(expireTime, {
    showHour,
    onExpire,
  });
  const theme = useTheme();
  const t = useTranslate();
  return (
    <Box
      component={Typography}
      variant="h4"
      flexGrow={1}
      color={theme.palette.error.main}
      sx={{
        fontSize: '1.225rem',
      }}
    >
      {expired
        ? t('common.text.expired')
        : t('resources.invoice.expireIn', {
            time,
          })}
    </Box>
  );
};
